import type { TotemTableSortState } from '@totem/vue';

import { getEspaceDistributeurServicesApi, getFileFromResponseOrFail } from '@/core/lib/ApiUtils';
import Cache from '@/core/lib/Cache';
import { DEFAULT_PAGE_SIZE, type PaginationApiResponse } from '@/core/lib/PaginationUtils';
import { downloadFile } from '@/domains/documentation/lib/FileDownloadUtils';

// Api types ----------------------------------------------------------------------------------------------------------

interface LibelleApi {
  libelle: string;
}

interface MetadataApi {
  themes: LibelleApi[];
  types: LibelleApi[];
  marches: LibelleApi[];
  offres: LibelleApi[];
}

interface NomApi {
  nom: string;
  nom_sans_extension: string;
}

interface MimeTypeApi {
  mime_type: string | null;
}

interface IsPublicApi {
  is_public: boolean;
}

interface DocumentApi extends NomApi, MimeTypeApi, IsPublicApi {
  id: string;
  date_modification: string;
  description: string | null;
  metadata: MetadataApi | null;
}

interface DocumentToDlApi extends NomApi, MimeTypeApi, IsPublicApi {
  id: string;
  size: number;
}

export type SearchResultApi = PaginationApiResponse<DocumentApi>;

interface DownloadManyPayloadApi {
  document_ids: string[];
}

// App types ----------------------------------------------------------------------------------------------------------

export interface Filters {
  marches?: string[];
  offres?: string[];
  types?: string[];
  themes?: string[];
}

interface Metadata {
  themes: string[];
  types: string[];
  marches: string[];
  offres: string[];
}

interface Nom {
  nom: string;
  nomSansExtension: string;
}

interface MimeType {
  mimeType?: string;
}

interface IsPublic {
  isPublic: boolean;
}

export interface Document extends Nom, MimeType, IsPublic {
  id: string;
  updatedAt: Date;
  description: string | undefined;
  metadata: Metadata;
}

export interface DocumentToDl extends Nom, MimeType, IsPublic {
  id: string;
  size: number;
}

export interface SearchResult {
  total: number;
  page: number;
  documents: Document[];
}

export const defaultSortState: Readonly<Required<TotemTableSortState>> = {
  key: 'nom',
  isDesc: false,
} as const;

function mapLibelles(list: LibelleApi[]): string[] {
  return list.map(({ libelle }) => libelle);
}

function mapMetadata(metadataApi: MetadataApi | undefined): Metadata {
  return {
    themes: metadataApi?.themes ? mapLibelles(metadataApi.themes) : [],
    types: metadataApi?.types ? mapLibelles(metadataApi.types) : [],
    marches: metadataApi?.marches ? mapLibelles(metadataApi.marches) : [],
    offres: metadataApi?.offres ? mapLibelles(metadataApi.offres) : [],
  };
}

function mapSearchResult({ content, page, total }: SearchResultApi): SearchResult {
  return {
    total,
    page,
    documents: content?.map((documentApi: DocumentApi) => ({
      id: documentApi.id,
      nom: documentApi.nom,
      nomSansExtension: documentApi.nom_sans_extension,
      mimeType: documentApi.mime_type ?? undefined,
      isPublic: documentApi.is_public,
      updatedAt: new Date(documentApi.date_modification),
      description: documentApi.description ?? undefined,
      metadata: mapMetadata(documentApi.metadata ?? undefined),
    })) ?? [],
  };
}

function mapDocumentToDownload(documentToDl: DocumentToDlApi): DocumentToDl {
  return {
    id: documentToDl.id,
    nom: documentToDl.nom,
    nomSansExtension: documentToDl.nom_sans_extension,
    mimeType: documentToDl.mime_type ?? undefined,
    isPublic: documentToDl.is_public,
    size: documentToDl.size,
  };
}

const appendFiltersQueryParams = (queryParams: URLSearchParams, filters: Filters) => {
  const { marches, offres, types, themes } = filters;

  for (const marche of marches ?? []) {
    queryParams.append('marches', marche);
  }

  for (const offre of offres ?? []) {
    queryParams.append('offres', offre);
  }

  for (const type of types ?? []) {
    queryParams.append('types', type);
  }

  for (const theme of themes ?? []) {
    queryParams.append('themes', theme);
  }
};

const buildSearchQueryParams = (
  keyword?: string,
  filters?: Filters,
  sort?: TotemTableSortState,
  page = 0,
): URLSearchParams => {
  const isDesc = sort?.isDesc ?? defaultSortState.isDesc;

  const queryParams = new URLSearchParams([
    ['page', page.toString()],
    ['page_size', DEFAULT_PAGE_SIZE.toString()],
    ['sort', sort?.key ?? defaultSortState.key],
    ['sort_direction', isDesc ? 'DESC' : 'ASC'],
  ]);

  if (keyword) {
    queryParams.set('recherche', keyword.trim());
  }

  if (filters) {
    appendFiltersQueryParams(queryParams, filters);
  }

  return queryParams;
};

export async function getSearchResult(
  keyword?: string,
  filters?: Filters,
  sort?: TotemTableSortState,
  page = 0,
): Promise<SearchResult> {
  const queryParams = buildSearchQueryParams(keyword, filters, sort, page);

  const api = await getEspaceDistributeurServicesApi();
  const { data } = await api.get<SearchResultApi>(`/documents?${queryParams.toString()}`);

  return mapSearchResult(data);
}

const cacheReferences = new Cache<Promise<string[]>>();

async function getReferences(name: string): Promise<string[]> {
  return cacheReferences.getOrSet(`references-${name}`, async () => {
    const api = await getEspaceDistributeurServicesApi();
    const { data } = await api.get<LibelleApi[]>(`/documents/${name}`);

    if (data.length === 0) {
      throw new Error(`Aucune référence "${name}" trouvée.`);
    }

    return mapLibelles(data);
  });
}

export async function getMarches(): Promise<string[]> {
  return getReferences('marche');
}

export async function getOffres(): Promise<string[]> {
  return getReferences('offre');
}

export async function getTypes(): Promise<string[]> {
  return getReferences('type');
}

export async function getDocumentToDownload(documentId: string): Promise<DocumentToDl> {
  const api = await getEspaceDistributeurServicesApi(false);
  const { data } = await api.get<DocumentToDlApi>(`/documents/${documentId}/public-info`);

  return mapDocumentToDownload(data);
}

export async function download(id: string, nom: string, isPublic: boolean): Promise<void> {
  const requiresAuth = !isPublic;
  const api = await getEspaceDistributeurServicesApi(requiresAuth);
  const url = isPublic ? `/documents/${id}/public-content` : `/documents/${id}/content`;

  const response = await api.get<ArrayBuffer>(url, { responseType: 'arraybuffer' });

  downloadFile(await getFileFromResponseOrFail(response));
}

export async function downloadMany(ids: string[]): Promise<void> {
  const payload: DownloadManyPayloadApi = { document_ids: ids };

  const response = await getEspaceDistributeurServicesApi().then(api =>
    api.post<ArrayBuffer>('/documents/multi-contents', payload, { responseType: 'arraybuffer' }),
  );

  downloadFile(await getFileFromResponseOrFail(response));
}

export async function getDocumentFile({ id, isPublic }: Document): Promise<File> {
  const requiresAuth = !isPublic;
  const api = await getEspaceDistributeurServicesApi(requiresAuth);
  const url = isPublic ? `/documents/${id}/public-content` : `/documents/${id}/content`;

  const response = await api.get<ArrayBuffer>(url, { responseType: 'arraybuffer' });

  return getFileFromResponseOrFail(response);
}
